import React, { useState, useCallback } from "react";
import {
  Cancel,
  ChevronLeft,
  ChevronRight,
  Photo,
  Video,
  Volume,
  VolumeMute,
} from "@/components/bnp-icons";
import { IconPhoto, IconVideo } from "@tabler/icons-react";

interface PhotoGridProps {
  allImages: string[];
}

const isVideoFile = (url: string): boolean => {
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
  if (!url) return false;
  return videoExtensions.some((ext) => url?.toLowerCase()?.endsWith(ext));
};

const MediaContent: React.FC<{ src?: string; className?: string }> = ({
  src,
  className,
}) => {
  if (isVideoFile(src ?? "")) {
    return <VideoContent src={src ?? ""} className={className} />;
  }
  return <img src={src} alt="Property view" className={className} />;
};

const VideoContent: React.FC<{ src: string; className?: string }> = ({
  src,
  className,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <video
        ref={videoRef}
        src={src}
        className={`block ${className}`}
        muted={true}
        autoPlay
        loop
        playsInline
      />
      <button
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute top-2 right-2 z-10 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80"
      >
        {muted ? <VolumeMute /> : <Volume />}
      </button>
    </div>
  );
};

const ImageModal: React.FC<{
  images: string[];
  initialIndex: number;
  onClose: () => void;
}> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    },
    [images.length],
  );

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + images.length) % images.length,
      );
    },
    [images.length],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex items-center justify-center max-w-[90%] max-h-[90%]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 z-10"
        >
          Close
        </button>

        {images.length > 1 && (
          <button
            onClick={handlePrev}
            className="absolute left-0 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 z-10"
          >
            <ChevronLeft />
          </button>
        )}

        <MediaContent
          src={images[currentIndex]}
          className="max-w-full max-h-full object-contain"
        />

        {images.length > 1 && (
          <button
            onClick={handleNext}
            className="absolute right-0 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 z-10"
          >
            <ChevronRight />
          </button>
        )}

        {images.length > 1 && (
          <div className="absolute -bottom-10 text-white">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

const PropertyPhotoGrid: React.FC<PhotoGridProps> = ({ allImages }) => {
  const images = allImages ?? [];
  const calculatedTotalVideos = images.filter(isVideoFile).length;
  const calculatedTotalPhotos = images.length - calculatedTotalVideos;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );

  const openImageModal = (index: number) => {
    if (!images[index]) {
      return;
    }
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
  };

  const displayImages =
    images.length > 0 ? images : ["/assets/images/image-placeholder.jpg"];
  const hasMultipleMedia = displayImages.length > 1;

  return (
    <div className="relative lg:w-[700px] font-sans">
      {selectedImageIndex !== null && (
        <ImageModal
          images={displayImages}
          initialIndex={selectedImageIndex}
          onClose={closeImageModal}
        />
      )}

      {hasMultipleMedia ? (
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-2 h-auto lg:h-[350px] w-full">
          {/* Main Featured Media (Left Side) */}
          {displayImages[0] && (
            <div
              className="lg:col-span-7 relative w-full h-[250px] sm:h-[300px] lg:h-full cursor-pointer overflow-hidden rounded"
              onClick={() => openImageModal(0)}
            >
              <MediaContent
                src={displayImages[0]}
                className="w-full h-full object-cover rounded"
              />
            </div>
          )}

          {/* Secondary Media Grid + Counter (Right Side) */}
          <div className="lg:col-span-3 flex flex-col gap-2 justify-between h-full overflow-hidden">
            {/* Upper Thumbnail (Image #2) */}
            {displayImages[1] && (
              <div
                className="hidden lg:block relative w-full h-full cursor-pointer overflow-hidden rounded"
                onClick={() => openImageModal(1)}
              >
                <MediaContent
                  src={displayImages[1]}
                  className="w-full h-full object-cover rounded"
                />
              </div>
            )}

            {/* Bottom Dual Thumbnails (Image #3 & #4) */}
            {(displayImages[2] || displayImages[3]) && (
              <div className="hidden lg:grid grid-cols-2 gap-2 h-[100px]">
                {displayImages[2] && (
                  <div
                    className="relative w-full h-full cursor-pointer overflow-hidden rounded"
                    onClick={() => openImageModal(2)}
                  >
                    <MediaContent
                      src={displayImages[2]}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
                {displayImages[3] && (
                  <div
                    className="relative w-full h-full cursor-pointer overflow-hidden rounded"
                    onClick={() => openImageModal(3)}
                  >
                    <MediaContent
                      src={displayImages[3]}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}
              </div>
            )}
            {/* Media Badges Footer */}
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-1.5 text-gray-700">
                <IconPhoto size={18} />
                <span className="text-xs font-medium whitespace-nowrap">
                  {calculatedTotalPhotos} Photo(s)
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-700">
                <IconVideo size={18} />
                <span className="text-xs font-medium whitespace-nowrap">
                  {calculatedTotalVideos} Video(s)
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="relative w-full h-[550px] cursor-pointer"
          onClick={() => openImageModal(0)}
        >
          <MediaContent
            src={displayImages[0]}
            className="w-full h-full object-cover rounded"
          />
        </div>
      )}
    </div>
  );
};

export default PropertyPhotoGrid;
